/**
 * Renders a notification from the backend registry alone: its translation key,
 * the record it points at, and the icon its category carries. Adding a
 * notification type is a registry entry plus two translations — no code here.
 *
 * Types whose target cannot be expressed as "a record id in the payload" declare
 * themselves in SPECIAL_ROUTES below; everything else is data.
 */
import legacy from './legacy';
import filters from '@/filters';
import { formatCurrency } from '@/utils/money';
import { categoryStyle } from './categoryStyle';

// Morph alias → route, for the polymorphic types (mentions) whose target is only
// known from the payload.
const COMMENTABLE_ROUTES = {
    contact: { preview: 'contact-preview', comments: 'contact-comments' },
    task: { preview: 'task-preview', comments: 'task-comments' },
    opportunity: { preview: 'opportunity-preview', comments: 'opportunity-comments' }
};

// Morph alias → preview route, for notifications that must fall back to the
// record an object was raised from.
const SOURCE_ROUTES = {
    contact: 'contact-preview',
    opportunity: 'opportunity-preview',
    project: 'project-preview',
    work_order: 'work-order-preview',
    quote: 'sales-quote-preview'
};

/**
 * The record a mention points at.
 *
 * A mention now reaches beyond comments — a record's description names people
 * the same way — so the payload carries `subject_*`. Rows stored before that
 * generalisation carry `commentable_*` and must keep working: a notification is
 * read months after it was written.
 */
export function mentionSubject(data = {}) {
    return {
        type: data.subject_type ?? data.commentable_type,
        id: data.subject_id ?? data.commentable_id
    };
}

// The handful of types whose destination is not "open record X". Each returns a
// router target, or null when it has none.
const SPECIAL_ROUTES = {
    'comment.mention': (data, org) => {
        const subject = mentionSubject(data);
        const routes = COMMENTABLE_ROUTES[subject.type];
        if (!routes || !subject.id) return null;
        // A mention in a comment lands on the comments tab, with the comment id so
        // the panel can scroll to it; one in a description has no comment to open,
        // so it lands on the record itself.
        return data.comment_id
            ? { name: routes.comments, params: { org, id: subject.id }, query: { comment: data.comment_id } }
            : { name: routes.preview, params: { org, id: subject.id } };
    },
    'comment.mention_access_requested': (data, org) => {
        const subject = mentionSubject(data);
        const routes = COMMENTABLE_ROUTES[subject.type];
        return routes && subject.id ? { name: routes.preview, params: { org, id: subject.id } } : null;
    },
    'mailbox.new_mail': data => data.link || null,
    // The inbox or the calendar, whichever the account serves: both carry the
    // Reconnect button.
    'connection.needs_reconnect': data => data.link || null,
    // A sync's summary names one fiche, or counts several: those open the list.
    'contact.became_customer_sync': (data, org) =>
        data.contact_id
            ? { name: 'contact-preview', params: { org, id: data.contact_id } }
            : { name: 'customers-list', params: { org } },
    // The setting to fix lives on a tab of the usage screen. The generic path
    // cannot carry a query, and landing on the first tab would leave the reader
    // hunting for the thing they were just told about. Its succeeding twin has
    // no such tab to reach: the balance it is about sits above them all.
    'billing.recharge_failed': (data, org) => ({
        name: 'settings-billing-usage',
        params: { org },
        query: { tab: 'recharge' }
    })
};

/**
 * Registry entry for a notification, or null when its type predates the registry.
 */
function definition(notification, types) {
    return (notification.type_key && types[notification.type_key]) || null;
}

/**
 * Translation key, completing `{placeholders}` from the payload so one type can
 * carry several texts (quote activities, RSVP answers, sync directions).
 */
function textKey(def, data) {
    return def.text_key.replace(/\{(\w+)\}/g, (_, key) => data[key] ?? '');
}

/**
 * Payload values rendered for humans, by naming convention: dates go through the
 * locale formatter, record numbers get the house six-digit padding, amounts the
 * same currency formatter every screen uses. Keeps the formatting rules in one
 * place instead of one branch per notification type.
 *
 * Money is formatted **here** rather than written into the payload by the sender:
 * a string baked server-side carried a neutral `110.00 CAD` into a bell whose
 * screens all said `110,00 $CA`, and no amount of care on one notification class
 * makes the next one agree.
 */
function formatParams(data) {
    const params = {};

    for (const [key, value] of Object.entries(data)) {
        if (value === null || value === undefined) {
            params[key] = '';
        } else if (/(_date|_at)$/.test(key)) {
            params[key] = filters.datetime(value);
        } else if (/_number$/.test(key)) {
            params[key] = String(value).padStart(6, '0');
        } else if (/(^|_)(amount|ceiling)$/.test(key) && Number.isFinite(Number(value))) {
            // The payload's own `currency` wins. Falling back to the reading
            // organization's is right for what it bills its own customers, and
            // wrong for everything Elixio bills *it*: those are in the back
            // office's currency, so a platform notification states it.
            //
            // Numbers only, and the guard is not defensive politeness: rows
            // written before this carry an already-formatted `"110.00 CAD"`,
            // which parses to NaN. Formatted anyway it read `0,00 £GB` — a sum
            // of zero, in a currency nobody had mentioned. Left alone it still
            // says what it always said. A notification is read months later.
            params[key] = formatCurrency(value, data.currency);
        } else {
            params[key] = value;
        }
    }

    return params;
}

export function notificationStyle(notification, types) {
    const def = definition(notification, types);

    if (!def) {
        return legacy.style(notification);
    }

    return categoryStyle(def.category);
}

export function notificationText(notification, types) {
    const def = definition(notification, types);
    const data = notification.data || {};

    if (!def) {
        return legacy.text(notification);
    }

    const key = textKey(def, data);
    const translated = i18n.t(key, formatParams(data));

    // A missing translation returns the key itself: better a readable fallback
    // than `notif_something_odd` in the user's face.
    return translated === key ? i18n.t('New notification') : translated;
}

/**
 * Router target for a notification, or null when it opens nothing.
 * @param {Object} notification
 * @param {Object} types - Registry presentation catalog
 * @param {String} org - Slug of the organization the notification belongs to
 * @param {Function} canAny - Ability check, to fall back when the record is out of reach
 */
export function notificationRoute(notification, types, org, canAny) {
    const def = definition(notification, types);
    const data = notification.data || {};

    if (!org) return null;

    if (!def) {
        return legacy.route(notification, org);
    }

    const special = SPECIAL_ROUTES[notification.type_key];
    if (special) {
        return special(data, org);
    }

    if (!def.route) return null;

    if (!def.entity) {
        return { name: def.route, params: { org } };
    }

    const id = data[`${def.entity}_id`];
    if (!id) return null;

    // Someone can be notified about a record they may not open — the sender of a
    // credit application, for one. Land them on the record it was raised from
    // rather than on a 403.
    if (def.entity === 'contract' && canAny && !canAny('contracts') && data.source_type && data.source_id) {
        const name = SOURCE_ROUTES[data.source_type];
        return name ? { name, params: { org, id: data.source_id } } : null;
    }

    return { name: def.route, params: { org, id } };
}

// Re-exported so a caller needs one import for "render this" and "how loudly".
export { raisesBadge, popsToast, staysInInbox } from './priority';

/**
 * The instant a notification is about.
 *
 * Usually that is when it was raised, but a type that knows better says so:
 * new mail is dated from when the message landed, not from when the poll a few
 * minutes later noticed it. `created_at` is stamped by the server on both the
 * stored row and its realtime twin, so the two never disagree.
 */
export function notificationTime(notification) {
    return notification.data?.occurred_at || notification.created_at || null;
}
